import { Chat, useChat } from "@ai-sdk/react";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import type { ExtensionArtifactAgentCoderPaneContext } from "@redotech/redo-web/extension-artifact-code-editor-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useRef } from "react";
import { AskRedoConversation } from "../app/ask-redo/components/ask-redo-conversation";
import { AskRedoPromptInput } from "../app/ask-redo/components/ask-redo-prompt-input";
import type { AskRedoUIMessage } from "../app/ask-redo/types";
import { ExtensionArtifactAgentCoderTransport } from "./extension-artifact-agent-coder-transport";

/** Agent Coder chat bound to the extension editor's live code state. */
export const ExtensionArtifactAgentCoderChat = memo(
  function ExtensionArtifactAgentCoderChat({
    contractId,
    inputSelector,
    code,
    setCode,
  }: ExtensionArtifactAgentCoderPaneContext) {
    const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

    // The Chat/transport pair is created once per mount, so live editor
    // state flows through refs: the transport reads them at send time.
    const editorStateRef = useRef({ contractId, inputSelector, code });
    editorStateRef.current = { contractId, inputSelector, code };
    const setCodeRef = useRef(setCode);
    setCodeRef.current = setCode;

    const chatInstance = useMemo(
      () =>
        new Chat<AskRedoUIMessage>({
          id: crypto.randomUUID(),
          transport: new ExtensionArtifactAgentCoderTransport(rpcClient, {
            getEditorState: () => editorStateRef.current,
            onCode: (newCode) => setCodeRef.current(newCode),
          }),
        }),
      [rpcClient],
    );
    const chat = useChat<AskRedoUIMessage>({ chat: chatInstance });
    const isStreaming =
      chat.status === "streaming" || chat.status === "submitted";

    return (
      <Flex dir="column" gap="md" grow={1} minH="none">
        <AskRedoConversation
          chat={chat}
          emptyState={
            // Fill the bare empty-state branch so the composer stays at the bottom.
            <Flex
              align="center"
              dir="column"
              gap="xs"
              grow={1}
              justify="center"
              minH="none"
              p="lg"
            >
              <Text fontSize="sm" fontWeight="medium">
                Build with Agent Coder
              </Text>
              <Text fontSize="xs" textAlign="center" textColor="secondary">
                Describe what the code should do and the assistant will write it
                into the editor, type-checking as it goes.
              </Text>
            </Flex>
          }
          isStreaming={isStreaming}
        />
        <Flex dir="column" shrink={0} w="full">
          <AskRedoPromptInput
            isStreaming={isStreaming}
            onSend={(text) => void chat.sendMessage({ text })}
            onStop={chat.stop}
            placeholder="Describe a change..."
          />
        </Flex>
      </Flex>
    );
  },
);
